/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * This source code is the exclusive property of SKD RATWANI Technology &
 * Software Solutions Pvt Ltd, operating as "SkillDelivery". Unauthorized
 * copying, modification, distribution, or use of this software, via any
 * medium, is strictly prohibited without express written permission.
 *
 * For licensing inquiries: legal@skilldelivery.co.in
 *
 * Registered Office: Sitapur, Uttar Pradesh, India - 261001
 * Jurisdiction: Noida, Uttar Pradesh, India - 201301
 */

import React, { useState, useEffect } from 'react';
import { Settings } from 'lucide-react';
import api from '../services/api';

const TrackSelector = ({ user, onTrackChange }) => {
  const [currentTrack, setCurrentTrack] = useState(user?.vertical || 'WMS');
  const [isChanging, setIsChanging] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  // Base tracks match today's hardcoded list for every unflagged user.
  // ShubbhYatra Phase 1: Travel is appended only when the authed user
  // already has vertical='Travel' (i.e. they're a ShubbhYatra rep) — the
  // selector then renders the correct label instead of silently falling
  // back to tracks[0] (WMS). Non-Travel users never see the Travel option.
  const BASE_TRACKS = [
    { value: 'WMS', label: 'Warehouse Management', icon: '📦', color: 'blue' },
    { value: 'iSeries', label: 'IBM iSeries/AS400', icon: '💻', color: 'purple' },
    { value: 'Civil', label: 'Civil & Construction', icon: '🏗️', color: 'orange' },
    { value: 'Stitching', label: 'Apparel & Stitching', icon: '🧵', color: 'pink' },
    { value: 'SkillCode', label: 'AI Software Engineering', icon: '🖥️', color: 'violet' }
  ];
  const tracks = (user?.vertical === 'Travel')
    ? [...BASE_TRACKS, { value: 'Travel', label: 'यात्रा / Travel', icon: '✈', color: 'cyan' }]
    : BASE_TRACKS;

  const selectedTrack = tracks.find(t => t.value === currentTrack) || tracks[0];

  const handleTrackChange = async (newTrack) => {
    setIsChanging(true);
    try {
      const response = await api.updateUser(user.id, {
        vertical: newTrack
      });

      if (response.success) {
        setCurrentTrack(newTrack);

        // Update localStorage
        const updatedUser = { ...user, vertical: newTrack };
        localStorage.setItem('user', JSON.stringify(updatedUser));

        // Notify parent component
        if (onTrackChange) {
          onTrackChange(newTrack);
        }

        setShowDropdown(false);
      }
    } catch (error) {
      console.error('Error updating track:', error);
      alert('Failed to update track. Please try again.');
    } finally {
      setIsChanging(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-4 mb-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`text-3xl p-2 bg-${selectedTrack.color}-50 rounded-lg`}>
            {selectedTrack.icon}
          </div>
          <div>
            <p className="text-sm text-gray-500">Your Primary Track</p>
            <p className="text-lg font-semibold text-gray-800">{selectedTrack.label}</p>
          </div>
        </div>

        <div className="relative">
          <button
            onClick={() => setShowDropdown(!showDropdown)}
            disabled={isChanging}
            className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 transition-colors disabled:opacity-50"
          >
            <Settings className="w-4 h-4" />
            <span className="text-sm font-medium">Change Track</span>
          </button>

          {showDropdown && (
            <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
              <div className="p-2">
                <p className="text-xs text-gray-500 px-3 py-2">Select your primary domain:</p>
                {tracks.map((track) => (
                  <button
                    key={track.value}
                    onClick={() => handleTrackChange(track.value)}
                    disabled={track.value === currentTrack || isChanging}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-md transition-colors ${
                      track.value === currentTrack
                        ? 'bg-blue-50 text-blue-600 cursor-default'
                        : 'hover:bg-gray-50 text-gray-700'
                    } disabled:opacity-50`}
                  >
                    <span className="text-2xl">{track.icon}</span>
                    <div className="flex-1 text-left">
                      <p className="text-sm font-medium">{track.label}</p>
                      {track.value === currentTrack && (
                        <p className="text-xs text-blue-500">Currently selected</p>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Track Description */}
      <div className="mt-4 pt-4 border-t border-gray-100">
        <p className="text-sm text-gray-600">
          {currentTrack === 'WMS' && 'Focus on warehouse operations, logistics, and inventory management systems.'}
          {currentTrack === 'iSeries' && 'Specialize in IBM AS/400, iSeries platforms, and enterprise systems.'}
          {currentTrack === 'Civil' && 'Expertise in construction, building design, and infrastructure planning.'}
          {currentTrack === 'Stitching' && 'Master apparel manufacturing, textile operations, and garment production.'}
        </p>
      </div>

      {/* Cross-Domain Note */}
      <div className="mt-3 bg-cyan-50 border border-cyan-100 rounded-md p-3">
        <p className="text-xs text-cyan-700">
          <span className="font-semibold">💡 Tip:</span> SkillBot can discuss topics from other domains too!
          Your primary track helps personalize your learning roadmap, but you can explore cross-domain topics anytime.
        </p>
      </div>
    </div>
  );
};

export default TrackSelector;
